<template>
	<div>
		<v-data-table
			class="logs-table"
			:no-data-text="$t('no-data-available')"
			:loading-text="$t('loading')"
			:headers="headers"
			:headers-length="headers.length"
			:items="filteredLogs"
			:loading="loader"
			sort-by="id"
			:search="appBarSearch"
			fixed-header
			dense
			sort-desc
			:height="pageHeight(174)"
			:footer-props="getDataTableFooterProps('systemLogsTable')"
			:items-per-page="logsPerPage"
			:expanded.sync="expanded"
			:single-expand="true"
			@update:items-per-page="onRowsPerPageChange">
			<template #item="{ item, isExpanded }">
				<tr @click="expand(item)" class="pointer" :class="[{ 'expanded-row': isExpanded }]">
					<td style="height: 30px !important" class="table-font-size">
						<div class="d-flex">
							<v-icon
								style="font-size: 18px !important"
								class="mr-2"
								v-if="item.level"
								:color="{ Info: 'green', Warn: 'orange', Error: 'red', Debug: 'green' }[item.level]">
								{{
									{ Info: 'mdi-information', Warn: 'mdi-alert', Error: 'mdi-close-circle', Debug: 'mdi-bug' }[
										item.level
									]
								}}
							</v-icon>
							{{ item.id }}
						</div>
					</td>
					<td style="height: 30px !important" class="table-font-size">
						{{ item.level }}
					</td>
					<td style="height: 30px !important; white-space: nowrap" class="table-font-size">
						{{
							new Date(item.logged).toLocaleString('en-US', {
								month: 'short',
								day: 'numeric',
								year: 'numeric',
								hour: 'numeric',
								minute: 'numeric',
								second: 'numeric',
								hour12: true,
								fractionalSecondDigits: 3,
							})
						}}
					</td>
					<td style="height: 30px !important; max-width: 50vw" class="table-font-size truncate-text">
						{{ item.message }}
					</td>
					<td style="height: 30px !important" class="table-font-size">
						{{ item.sessionGuid }}
					</td>
					<!--<td style="height:30px !important;" class="table-font-size px-2">-->
					<!--{{ item.logger }}-->
					<!--</td>-->
				</tr>
			</template>
			<template #expanded-item>
				<tr style="cursor: default; background-color: #fff !important">
					<td class="pa-3" :colspan="8">
						<v-data-table
							@click:row="getDetails"
							:items="secondaryLogs"
							:headers="headerDetails"
							dense
							:items-per-page="-1"
							hide-default-footer
							class="data-table-outline"
							fixed-header />
					</td>
				</tr>
			</template>
			<template #[`footer.prepend`]>
				<span class="font-weight-bold mx-3">{{ $t('table-legend') }}:</span>
				<i class="mdi mdi-reload mdi-18px d-flex px-2 align-center pointer" @click="filterDataTable('All')">
					<span class="color-black">{{ $t('all') }}</span>
				</i>
				<i
					class="mdi mdi-information mdi-18px color-green px-2 d-flex align-center pointer"
					@click="filterDataTable('Info')">
					<span class="color-black">{{ getLogCount('Info') }}</span>
				</i>
				<i
					class="mdi mdi-alert mdi-18px color-orange px-2 d-flex align-center pointer"
					@click="filterDataTable('Warn')">
					<span class="color-black">{{ getLogCount('Warn') }}</span>
				</i>
				<i
					class="mdi mdi-close-circle mdi-18px color-red px-2 d-flex align-center pointer"
					@click="filterDataTable('Error')">
					<span class="color-black">{{ getLogCount('Error') }}</span>
				</i>
				<i class="mdi mdi-bug mdi-18px color-green px-2 d-flex align-center pointer" @click="filterDataTable('Debug')">
					<span class="color-black">{{ getLogCount('Debug') }}</span>
				</i>
				<v-select
					v-model="logLevel"
					:items="logLevels"
					:label="$t('log-level')"
					class="ml-2"
					hide-details
					item-text="shortDescription"
					dense
					outlined
					style="max-width: 200px"
					@input="selectLogLevel"
					:menu-props="{ top: true, offsetY: true }">
					<template #item="{ item }">
						<v-tooltip right>
							<template #activator="{ on, attrs }">
								<div class="text-center" style="width: 100%" v-bind="attrs" v-on="on">
									{{ $i18n.locale === 'en' ? item.shortDescription : item.shortDescriptionEsp }}
								</div>
							</template>
							<span>{{ $i18n.locale === 'en' ? item.longDescription : item.longDescriptionEsp }}</span>
						</v-tooltip>
					</template>
					<template #selection="{ item }">
						{{ $i18n.locale === 'en' ? item.shortDescription : item.shortDescriptionEsp }}
					</template>
				</v-select>
			</template>
		</v-data-table>
		<v-dialog v-if="dialog" v-model="dialog" width="auto">
			<v-card style="min-height: 50vh; max-height: 80vh; width: 33vw; min-width: 400px" class="d-flex flex-column">
				<v-card-title class="card-header text-left fixed-card-title2">
					<v-row no-gutters>
						<v-col class="align-center d-flex" cols="10">
							<v-toolbar-title>{{ $t('log-details') }}</v-toolbar-title>
						</v-col>
						<v-spacer />
						<v-icon @click="dialog = false" class="white-icon">mdi-close</v-icon>
					</v-row>
				</v-card-title>
				<v-card-text class="pa-4 flex-grow-1 overflow-auto d-flex flex-column">
					<v-row dense class="text-left">
						<v-col cols="12">
							<div class="text-subtitle-1 font-weight-bold">{{ $t('timestamp') }}:</div>
							<div class="text-body-1">
								{{
									new Date(logById.logged).toLocaleString('en-US', {
										month: 'short',
										day: 'numeric',
										year: 'numeric',
										hour: 'numeric',
										minute: 'numeric',
										second: 'numeric',
										hour12: true,
										fractionalSecondDigits: 3,
									})
								}}
							</div>
						</v-col>
						<v-col cols="12">
							<div class="text-subtitle-1 font-weight-bold">{{ $t('logger') }}:</div>
							<div class="text-body-1">{{ logById?.logger }}</div>
						</v-col>
						<v-col cols="12">
							<div class="text-subtitle-1 font-weight-bold">{{ $t('call-site') }}:</div>
							<div class="text-body-1">{{ logById?.callsite }}</div>
						</v-col>
						<v-col cols="12" v-if="logById?.request">
							<div class="text-subtitle-1 font-weight-bold">{{ $t('request') }}:</div>
							<div class="text-body-1">{{ logById?.request }}</div>
						</v-col>
						<v-col cols="12">
							<div class="text-subtitle-1 font-weight-bold">{{ $t('message') }}:</div>
							<div class="text-body-1">{{ logById?.message }}</div>
						</v-col>
						<v-col cols="12" v-if="logById?.exception" style="max-height: 200px; overflow-y: auto">
							<div class="text-subtitle-1 font-weight-bold">{{ $t('exception') }}:</div>
							<div class="text-body-1">{{ logById?.exception }}</div>
						</v-col>
					</v-row>
				</v-card-text>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import LogsRepository from '@/data/repositories/v1/LogsRepository';
import { getDataTableFooterProps } from '@/utils/utilities';

export default {
	data () {
		return {
			filterType: '',
			logs: [],
			secondaryLogs: [],
			expanded: [],
			loader: false,
			logById: null,
			selectedLogDate: new Date().toISOString().substring(0, 10),
			logsPerPage: -1,
			dialog: false,
			logLevel: '',
		};
	},
	mounted () {
		this.fetchLogs();
	},
	methods: {
		...mapActions({
			getLogLevels: 'logLevels/getLogLevels',
			setLogLevel: 'logLevels/setLogLevel',
		}),
		getDataTableFooterProps,
		filterDataTable (type) {
			this.filterType = type; // Update the filter type
		},
		onRowsPerPageChange (newValue) {
			this.logsPerPage = newValue;
			this.fetchLogs();

			// Handle additional logic when rows per page changes
		},
		async expand (item) {
			if (this.expanded[0] === item) this.expanded = [];
			else {
				await LogsRepository.getLogWithin2Seconds(item.id).then((res) => {
					this.expanded = [item];
					this.secondaryLogs = res.data;
				});
			}
		},
		async getDetails (item) {
			await this.getLogById(item.id);
			this.dialog = true;
		},
		async fetchLogs () {
			const headers = {
				MaxSystemLogs: this.logsPerPage,
				SelectedDate: this.selectedLogDate,
			};
			this.loader = true;
			const [res] = await Promise.all([LogsRepository.getLogs(headers), this.getLogLevels()]);
			this.logs = res.data;
			this.logLevel = this.logLevels.find((level) => level.isActive)?.shortDescription;
			this.loader = false;
		},
		async getLogById (id) {
			this.loader = true;
			const res = await LogsRepository.getLogById(id);
			this.logById = res.data;
			this.loader = false;
		},
		getLogCount (level) {
			// Ensure logs and aggregatedLogsData are defined before trying to find
			if (!this.logs || !this.logs.aggregatedLogsData) {
				return 0; // Return 0 if the data isn't loaded yet
			}

			const log = this.logs.aggregatedLogsData.find((log) => log.level === level);
			return log ? log.counts : 0; // Return the count or 0 if not found
		},
		async selectLogLevel (logLevel) {
			this.loader = true;
			await this.setLogLevel(logLevel);
			this.loader = false;
		},
	},
	computed: {
		...mapState({
			selectedLogsDate: (state) => state.filterbar.selectedLogsDate,
			logLevels: (state) => state.logLevels.logLevels,
			// logs: (state) => state.logs.logs,
		}),
		...mapGetters({
			pageHeight: 'pageHeight',
			appBarSearch: 'filterbar/getSearch',
		}),
		headers () {
			return [
				{ text: this.$t('id'), value: 'id' },
				{ text: this.$t('level'), value: 'level' },
				{ text: this.$t('logged'), value: 'logged' },
				{ text: this.$t('message'), value: 'message', width: '400px' },
				{ text: this.$t('session'), value: 'sessionGuid' },
			];
		},
		headerDetails () {
			return [
				{ text: this.$t('id'), value: 'id' },
				{ text: this.$t('level'), value: 'level' },
				{ text: this.$t('logged'), value: 'logged' },
				{ text: this.$t('message'), value: 'message', width: '400px' },
				{ text: this.$t('session'), value: 'sessionGuid' },
			];
		},
		filteredLogs () {
			if (!this.filterType) return this.logs.systemLogData;
			if (this.filterType === 'All') {
				return this.logs.systemLogData;
			} else {
				return this.logs.systemLogData.filter((log) => log.level === this.filterType);
			}
		},
	},
	watch: {
		selectedLogsDate: {
			handler (newVal) {
				this.selectedLogDate = newVal;
				this.fetchLogs();
			},
			deep: true,
		},
	},
};
</script>

<style>
.table-font-size {
	font-size: 14px;
}

.v-messages {
	display: none !important;
}

.small i {
	font-size: 18px !important;
}

.small [class*='__ripple'] {
	left: 0;
	display: none;
}
</style>
